/**
 * TwoFactorSetup — TOTP 2FA Enable / Disable Flow
 * ════════════════════════════════════════════════
 * Used inside the Security page of Mentor & Mentee dashboards.
 *
 * Props:
 *   user       — current user object from AuthContext
 *   onUpdate   — (patch) => void  called when 2FA state changes
 *   onClose    — () => void       close the modal
 *
 * Steps (enable):
 *   1 → QR Code scan
 *   2 → Enter code to verify
 *   3 → Save backup codes
 *   4 → Done ✅
 *
 * Steps (disable):
 *   1 → Enter current code to confirm
 *   2 → Done (disabled)
 *
 * TODO (Backend):
 *   POST /api/v1/2fa/setup        → returns { qrCode, manualKey }
 *   POST /api/v1/2fa/verify-setup → { code } → enables 2FA, returns { backupCodes }
 *   POST /api/v1/2fa/disable      → { code } → disables 2FA
 *   POST /api/v1/2fa/backup-codes → regenerate backup codes
 */
import React, { useState, useEffect, useRef } from 'react';
import securityService from '../services/securityService';

// ── Mock QR code data URL (solid black square placeholder) ─────
// TODO: Replace with real QR from POST /api/v1/2fa/setup
const MOCK_QR = 'data:image/svg+xml,' + encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
  <rect width="200" height="200" fill="white"/>
  <text x="100" y="88" text-anchor="middle" font-size="13" fill="#374151" font-family="monospace" font-weight="bold">Scan with</text>
  <text x="100" y="108" text-anchor="middle" font-size="13" fill="#374151" font-family="monospace" font-weight="bold">Authenticator</text>
  <text x="100" y="128" text-anchor="middle" font-size="12" fill="#9ca3af" font-family="monospace">App (Mock QR)</text>
  <rect x="20" y="20" width="40" height="40" fill="none" stroke="#1a1a2e" stroke-width="4"/>
  <rect x="28" y="28" width="24" height="24" fill="#1a1a2e"/>
  <rect x="140" y="20" width="40" height="40" fill="none" stroke="#1a1a2e" stroke-width="4"/>
  <rect x="148" y="28" width="24" height="24" fill="#1a1a2e"/>
  <rect x="20" y="140" width="40" height="40" fill="none" stroke="#1a1a2e" stroke-width="4"/>
  <rect x="28" y="148" width="24" height="24" fill="#1a1a2e"/>
  <rect x="75" y="20" width="8" height="8" fill="#1a1a2e"/>
  <rect x="90" y="20" width="8" height="8" fill="#1a1a2e"/>
  <rect x="105" y="20" width="8" height="8" fill="#1a1a2e"/>
  <rect x="75" y="35" width="8" height="8" fill="#1a1a2e"/>
  <rect x="105" y="35" width="8" height="8" fill="#1a1a2e"/>
  <rect x="140" y="75" width="8" height="8" fill="#1a1a2e"/>
  <rect x="155" y="75" width="8" height="8" fill="#1a1a2e"/>
  <rect x="170" y="75" width="8" height="8" fill="#1a1a2e"/>
</svg>`);

const MOCK_MANUAL_KEY = 'JBSWY3DPEHPK3PXP'; // TODO: from backend

// ── Generate mock backup codes ─────────────────────────────────
function makeMockBackupCodes() {
  const hex = () => Math.floor(Math.random()*65536).toString(16).toUpperCase().padStart(4,'0');
  return Array.from({length:10}, () => `${hex()}-${hex()}`);
}

// ── Single digit input box ─────────────────────────────────────
function DigitInput({ value, inputRef, onChange, onKeyDown, onPaste }) {
  return (
    <input
      ref={inputRef}
      value={value}
      maxLength={1}
      onChange={onChange}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      style={{
        width:44, height:52, textAlign:'center', fontSize:'1.4rem', fontWeight:900,
        border:'2px solid #e5e7eb', borderRadius:10, fontFamily:'monospace',
        color:'#1a1a2e', background: value ? '#f0fdf4' : '#fff',
        borderColor: value ? '#1DB954' : '#e5e7eb',
        outline:'none', transition:'all .15s', caretColor:'transparent',
      }}
    />
  );
}

// ── 6-box OTP input ────────────────────────────────────────────
function OtpInput({ value, onChange }) {
  // Hooks must be at top level — create 6 refs individually
  const r0 = useRef(null), r1 = useRef(null), r2 = useRef(null);
  const r3 = useRef(null), r4 = useRef(null), r5 = useRef(null);
  const refs = [r0, r1, r2, r3, r4, r5];
  const digits = value.split('').concat(Array(6).fill('')).slice(0,6);

  const handleChange = (i, e) => {
    const v = e.target.value.replace(/\D/g,'');
    if (!v) return;
    const next = [...digits];
    next[i] = v[v.length-1];
    onChange(next.join(''));
    if (i < 5) refs[i+1].current?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace') {
      const next = [...digits];
      if (digits[i]) { next[i] = ''; onChange(next.join('')); }
      else if (i > 0) { next[i-1] = ''; onChange(next.join('')); refs[i-1].current?.focus(); }
    } else if (e.key === 'ArrowLeft' && i > 0) refs[i-1].current?.focus();
    else if (e.key === 'ArrowRight' && i < 5) refs[i+1].current?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
    if (pasted) { onChange(pasted.padEnd(6,'').slice(0,6)); refs[Math.min(pasted.length,5)].current?.focus(); }
  };

  return (
    <div style={{display:'flex', gap:8, justifyContent:'center'}}>
      {digits.map((d,i) => (
        <DigitInput key={i} value={d} inputRef={refs[i]}
          onChange={e => handleChange(i,e)}
          onKeyDown={e => handleKeyDown(i,e)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
}

// ── CSS ────────────────────────────────────────────────────────
const CSS = `
  @keyframes tfa-slide { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
  @keyframes tfa-pop   { 0%{transform:scale(0)} 60%{transform:scale(1.15)} 100%{transform:scale(1)} }
  @keyframes tfa-spin  { to{transform:rotate(360deg)} }
  .tfa-overlay { position:fixed;inset:0;background:rgba(15,15,30,.75);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px;backdrop-filter:blur(5px) }
  .tfa-modal   { background:#fff;border-radius:24px;width:100%;max-width:480px;max-height:92vh;overflow-y:auto;box-shadow:0 32px 80px rgba(0,0,0,.28);animation:tfa-slide .35s cubic-bezier(.34,1.56,.64,1) }
  .tfa-step    { animation:tfa-slide .3s ease }
  .tfa-backup  { font-family:monospace;padding:9px 14px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:9px;font-size:.85rem;font-weight:700;color:#1a1a2e;letter-spacing:.05em }
  .tfa-backup.used { opacity:.4;text-decoration:line-through }
`;

export default function TwoFactorSetup({ user, onUpdate, onClose }) {
  const mode = user?.twoFactor?.enabled ? 'disable' : 'enable';
  const [step,        setStep]        = useState(1);
  const [code,        setCode]        = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error,       setError]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const [copied,      setCopied]      = useState(false);

  // Real QR / manual key from POST /2fa/setup
  const [qrCode,       setQrCode]      = useState(null);
  const [manualKey,    setManualKey]   = useState(MOCK_MANUAL_KEY);
  const [setupLoading, setSetupLoading] = useState(false);

  useEffect(() => { setCode(''); setError(''); }, [step]);

  // Fetch QR code when modal opens in enable mode
  useEffect(() => {
    if (mode !== 'enable') return;
    setSetupLoading(true);
    securityService.setup2FA()
      .then(res => {
        if (res.qrCode)    setQrCode(res.qrCode);
        if (res.manualKey) setManualKey(res.manualKey);
      })
      .catch(() => {}) // fallback to MOCK_QR / MOCK_MANUAL_KEY
      .finally(() => setSetupLoading(false));
  }, []); // eslint-disable-line

  const handleEnable = async () => {
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setError('Invalid code. Check your Authenticator app.');
      return;
    }
    setLoading(true);
    try {
      const res = await securityService.verify2FA(code);
      setBackupCodes(res.backupCodes || makeMockBackupCodes());
      onUpdate({ twoFactor: { enabled: true, method: 'totp', enabledAt: new Date().toISOString(), backupCodesCount: 10 } });
      setStep(3);
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setError('Invalid code. Enter your current Authenticator code.');
      return;
    }
    setLoading(true);
    try {
      await securityService.disable2FA(code);
      onUpdate({ twoFactor: { enabled: false, method: null, enabledAt: null } });
      setStep(2);
    } catch (err) {
      setError(err.message || 'Failed to disable 2FA. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyBackups = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadBackups = () => {
    const blob = new Blob([
      `DevBrains — 2FA Backup Codes\nGenerated: ${new Date().toLocaleDateString()}\n\n${backupCodes.join('\n')}\n\nEach code can only be used once. Store them safely.`
    ], { type:'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'devbrains-backup-codes.txt';
    a.click();
  };

  return (
    <div className="tfa-overlay" onClick={onClose}>
      <style>{CSS}</style>
      <div className="tfa-modal" onClick={e => e.stopPropagation()}>

        {/* ═══════════════════════════════════
            ENABLE — STEP 1: Scan QR Code
            ═══════════════════════════════════ */}
        {mode==='enable' && step===1 && (
          <div className="tfa-step">
            {/* Header */}
            <div style={{background:'linear-gradient(135deg,#1a1a2e,#16213e)',padding:'24px 28px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                <div style={{color:'rgba(255,255,255,.6)',fontSize:'.65rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em'}}>Step 1 of 3 · Scan QR Code</div>
                <button onClick={onClose} style={{background:'none',border:'none',color:'rgba(255,255,255,.5)',cursor:'pointer',fontSize:'1.2rem',lineHeight:1}}>×</button>
              </div>
              <div style={{color:'#fff',fontWeight:900,fontSize:'1.1rem'}}>Set Up Authenticator App</div>
              {/* Progress */}
              <div style={{display:'flex',gap:5,marginTop:14}}>
                {[1,2,3].map(i=>(
                  <div key={i} style={{flex:1,height:3,borderRadius:100,background:i<=1?'#1DB954':'rgba(255,255,255,.2)',transition:'background .3s'}}/>
                ))}
              </div>
            </div>

            <div style={{padding:'24px 28px'}}>
              {/* App badges */}
              <div style={{display:'flex',gap:8,marginBottom:20,flexWrap:'wrap'}}>
                {[['📱','Google Authenticator'],['🔐','Authy'],['🪟','Microsoft Authenticator']].map(([ic,lbl])=>(
                  <span key={lbl} style={{padding:'4px 10px',borderRadius:100,background:'#f3f4f6',fontSize:'.68rem',fontWeight:700,color:'#374151'}}>{ic} {lbl}</span>
                ))}
              </div>

              {/* Instructions */}
              <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:10,padding:'11px 14px',marginBottom:18}}>
                <div style={{fontSize:'.73rem',color:'#166534',fontWeight:800,marginBottom:4}}>How to scan:</div>
                <ol style={{margin:0,padding:'0 0 0 16px',fontSize:'.72rem',color:'#166534',lineHeight:1.9}}>
                  <li>Open your Authenticator app</li>
                  <li>Tap <strong>+</strong> or <strong>Add Account</strong></li>
                  <li>Choose <strong>Scan QR code</strong></li>
                  <li>Point camera at the QR code below</li>
                </ol>
              </div>

              {/* QR Code */}
              <div style={{textAlign:'center',marginBottom:16}}>
                <div style={{display:'inline-block',padding:12,border:'2px solid #e5e7eb',borderRadius:16,background:'#fff',boxShadow:'0 4px 16px rgba(0,0,0,.06)',position:'relative'}}>
                  {setupLoading
                    ? <div style={{width:180,height:180,display:'flex',alignItems:'center',justifyContent:'center'}}>
                        <span style={{width:28,height:28,border:'3px solid #e5e7eb',borderTopColor:'#1DB954',borderRadius:'50%',display:'inline-block',animation:'tfa-spin .6s linear infinite'}}/>
                      </div>
                    : <img src={qrCode || MOCK_QR} alt="QR Code" style={{width:180,height:180,display:'block'}}/>}
                </div>
              </div>

              {/* Manual key */}
              <details style={{marginBottom:20}}>
                <summary style={{cursor:'pointer',fontSize:'.78rem',color:'#2563eb',fontWeight:700,userSelect:'none'}}>
                  Can't scan? Enter key manually →
                </summary>
                <div style={{marginTop:10,padding:'12px 14px',background:'#f8fafc',borderRadius:10,border:'1px solid #e5e7eb'}}>
                  <div style={{fontSize:'.65rem',color:'#9ca3af',fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>Account Name</div>
                  <div style={{fontSize:'.82rem',fontWeight:700,color:'#374151',marginBottom:10}}>DevBrains ({user?.email})</div>
                  <div style={{fontSize:'.65rem',color:'#9ca3af',fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>Secret Key</div>
                  <code style={{display:'block',padding:'8px 12px',background:'#fff',border:'1px solid #e5e7eb',borderRadius:8,fontSize:'.82rem',fontWeight:800,color:'#1a1a2e',letterSpacing:'.15em',wordBreak:'break-all'}}>
                    {manualKey}
                  </code>
                  <div style={{fontSize:'.65rem',color:'#9ca3af',marginTop:6}}>Time based (TOTP) · SHA1 · 6 digits · 30 seconds</div>
                </div>
              </details>

              <button onClick={() => setStep(2)} style={{width:'100%',padding:'13px',borderRadius:12,border:'none',background:'#1DB954',color:'#fff',fontFamily:'inherit',fontWeight:900,fontSize:'.92rem',cursor:'pointer'}}>
                I've Scanned It — Next →
              </button>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════
            ENABLE — STEP 2: Verify Code
            ═══════════════════════════════════ */}
        {mode==='enable' && step===2 && (
          <div className="tfa-step">
            <div style={{background:'linear-gradient(135deg,#1a1a2e,#16213e)',padding:'24px 28px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                <div style={{color:'rgba(255,255,255,.6)',fontSize:'.65rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em'}}>Step 2 of 3 · Verify Code</div>
                <button onClick={onClose} style={{background:'none',border:'none',color:'rgba(255,255,255,.5)',cursor:'pointer',fontSize:'1.2rem',lineHeight:1}}>×</button>
              </div>
              <div style={{color:'#fff',fontWeight:900,fontSize:'1.1rem'}}>Enter 6-Digit Code</div>
              <div style={{display:'flex',gap:5,marginTop:14}}>
                {[1,2,3].map(i=>(
                  <div key={i} style={{flex:1,height:3,borderRadius:100,background:i<=2?'#1DB954':'rgba(255,255,255,.2)',transition:'background .3s'}}/>
                ))}
              </div>
            </div>

            <div style={{padding:'28px'}}>
              <p style={{fontSize:'.87rem',color:'#374151',lineHeight:1.65,marginTop:0,marginBottom:24}}>
                Open your Authenticator app and enter the <strong>6-digit code</strong> for DevBrains:
              </p>

              <OtpInput value={code} onChange={setCode}/>

              {error && (
                <div style={{marginTop:12,padding:'9px 13px',background:'#fef2f2',border:'1px solid #fecaca',borderRadius:9,fontSize:'.78rem',color:'#dc2626',fontWeight:600}}>
                  ⚠️ {error}
                </div>
              )}

              <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:9,padding:'9px 13px',marginTop:14,marginBottom:20,fontSize:'.72rem',color:'#92400e'}}>
                💡 <strong>Demo tip:</strong> Enter any 6 digits (e.g. <code>123456</code>) to proceed
              </div>

              <div style={{display:'flex',gap:10}}>
                <button onClick={()=>setStep(1)} style={{padding:'13px 18px',borderRadius:12,border:'1.5px solid #e5e7eb',background:'#fff',fontFamily:'inherit',color:'#6b7280',cursor:'pointer',fontWeight:600}}>← Back</button>
                <button onClick={handleEnable} disabled={code.length!==6||loading}
                  style={{flex:1,padding:'13px',borderRadius:12,border:'none',fontFamily:'inherit',fontWeight:900,fontSize:'.9rem',
                    cursor:code.length===6?'pointer':'not-allowed',
                    background:code.length===6?'#1DB954':'#e5e7eb',
                    color:code.length===6?'#fff':'#9ca3af',transition:'all .2s'}}>
                  {loading
                    ? <span style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
                        <span style={{width:14,height:14,border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',display:'inline-block',animation:'tfa-spin .6s linear infinite'}}/>
                        Verifying…
                      </span>
                    : '✅ Verify & Enable 2FA'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════
            ENABLE — STEP 3: Save Backup Codes
            ═══════════════════════════════════ */}
        {mode==='enable' && step===3 && (
          <div className="tfa-step">
            <div style={{background:'linear-gradient(135deg,#f59e0b,#d97706)',padding:'24px 28px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                <div style={{color:'rgba(255,255,255,.85)',fontSize:'.65rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em'}}>Step 3 of 3 · Save Backup Codes</div>
                <button onClick={onClose} style={{background:'none',border:'none',color:'rgba(255,255,255,.6)',cursor:'pointer',fontSize:'1.2rem',lineHeight:1}}>×</button>
              </div>
              <div style={{color:'#fff',fontWeight:900,fontSize:'1.1rem'}}>Save Your Backup Codes</div>
              <div style={{display:'flex',gap:5,marginTop:14}}>
                {[1,2,3].map(i=>(<div key={i} style={{flex:1,height:3,borderRadius:100,background:'rgba(255,255,255,.9)',transition:'background .3s'}}/>))}
              </div>
            </div>

            <div style={{padding:'24px 28px'}}>
              <div style={{background:'#fef3c7',border:'1px solid #fde68a',borderRadius:10,padding:'12px 14px',marginBottom:18}}>
                <div style={{fontWeight:800,color:'#92400e',fontSize:'.82rem',marginBottom:4}}>⚠️ Save these now — you won't see them again</div>
                <div style={{fontSize:'.73rem',color:'#b45309',lineHeight:1.6}}>
                  If you lose access to your Authenticator app, use these codes to log in. Each code works only once.
                </div>
              </div>

              {/* Backup codes grid */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:7,marginBottom:16}}>
                {backupCodes.map((c,i)=>(
                  <div key={i} className="tfa-backup">
                    <span style={{color:'#9ca3af',fontSize:'.65rem',marginRight:6}}>{String(i+1).padStart(2,'0')}.</span>{c}
                  </div>
                ))}
              </div>

              {/* Action buttons */}
              <div style={{display:'flex',gap:8,marginBottom:20}}>
                <button onClick={copyBackups}
                  style={{flex:1,padding:'10px',borderRadius:10,border:'1.5px solid #e5e7eb',background: copied?'#f0fdf4':'#fff',fontFamily:'inherit',fontWeight:700,fontSize:'.8rem',cursor:'pointer',color:copied?'#16a34a':'#374151',transition:'all .2s'}}>
                  {copied ? '✅ Copied!' : '📋 Copy All'}
                </button>
                <button onClick={downloadBackups}
                  style={{flex:1,padding:'10px',borderRadius:10,border:'1.5px solid #e5e7eb',background:'#fff',fontFamily:'inherit',fontWeight:700,fontSize:'.8rem',cursor:'pointer',color:'#374151'}}>
                  ⬇️ Download .txt
                </button>
              </div>

              <button onClick={()=>setStep(4)}
                style={{width:'100%',padding:'13px',borderRadius:12,border:'none',background:'#1DB954',color:'#fff',fontFamily:'inherit',fontWeight:900,fontSize:'.9rem',cursor:'pointer'}}>
                ✅ I've Saved My Codes — Finish
              </button>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════
            ENABLE — STEP 4: Success
            ═══════════════════════════════════ */}
        {mode==='enable' && step===4 && (
          <div className="tfa-step" style={{padding:'44px 32px',textAlign:'center'}}>
            <div style={{fontSize:70,lineHeight:1,marginBottom:16,animation:'tfa-pop .5s cubic-bezier(.34,1.56,.64,1)'}}>🔐</div>
            <h2 style={{fontWeight:900,fontSize:'1.5rem',color:'#111827',margin:'0 0 10px',letterSpacing:'-.4px'}}>
              Two-Factor Auth Enabled!
            </h2>
            <p style={{color:'#6b7280',lineHeight:1.7,marginBottom:4}}>
              Your account is now protected with TOTP authentication.
              Every login will require your Authenticator app.
            </p>
            <div style={{display:'flex',justifyContent:'center',gap:16,margin:'24px 0',flexWrap:'wrap'}}>
              {[['🔑','TOTP Active'],['💾','10 Backup Codes'],['📱','App Linked']].map(([ic,lbl])=>(
                <div key={lbl} style={{padding:'8px 16px',background:'#f0fdf4',borderRadius:100,fontSize:'.78rem',fontWeight:700,color:'#16a34a',border:'1px solid #bbf7d0'}}>
                  {ic} {lbl}
                </div>
              ))}
            </div>
            <button onClick={onClose}
              style={{padding:'12px 40px',borderRadius:12,border:'none',background:'#1DB954',color:'#fff',fontFamily:'inherit',fontWeight:900,fontSize:'.92rem',cursor:'pointer'}}>
              🎯 Done
            </button>
          </div>
        )}

        {/* ═══════════════════════════════════
            DISABLE — STEP 1: Confirm Code
            ═══════════════════════════════════ */}
        {mode==='disable' && step===1 && (
          <div className="tfa-step">
            <div style={{background:'linear-gradient(135deg,#ef4444,#dc2626)',padding:'24px 28px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                <div style={{color:'rgba(255,255,255,.8)',fontSize:'.65rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'.08em'}}>Security Verification</div>
                <button onClick={onClose} style={{background:'none',border:'none',color:'rgba(255,255,255,.5)',cursor:'pointer',fontSize:'1.2rem',lineHeight:1}}>×</button>
              </div>
              <div style={{color:'#fff',fontWeight:900,fontSize:'1.1rem'}}>Disable Two-Factor Auth</div>
            </div>

            <div style={{padding:'28px'}}>
              <div style={{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:10,padding:'12px 14px',marginBottom:20}}>
                <div style={{fontWeight:800,color:'#dc2626',fontSize:'.82rem',marginBottom:4}}>⚠️ This will reduce your account security</div>
                <div style={{fontSize:'.73rem',color:'#b91c1c',lineHeight:1.6}}>
                  Anyone with your password will be able to access your account without a second step.
                </div>
              </div>

              <p style={{fontSize:'.87rem',color:'#374151',marginBottom:20}}>
                Enter the 6-digit code from your Authenticator app to confirm:
              </p>

              <OtpInput value={code} onChange={setCode}/>

              {error && (
                <div style={{marginTop:12,padding:'9px 13px',background:'#fef2f2',border:'1px solid #fecaca',borderRadius:9,fontSize:'.78rem',color:'#dc2626',fontWeight:600}}>
                  ⚠️ {error}
                </div>
              )}

              <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:9,padding:'9px 13px',marginTop:12,marginBottom:20,fontSize:'.72rem',color:'#92400e'}}>
                💡 <strong>Demo:</strong> Enter any 6 digits to proceed
              </div>

              <div style={{display:'flex',gap:10}}>
                <button onClick={onClose} style={{padding:'13px 18px',borderRadius:12,border:'1.5px solid #e5e7eb',background:'#fff',fontFamily:'inherit',color:'#6b7280',cursor:'pointer',fontWeight:600}}>Cancel</button>
                <button onClick={handleDisable} disabled={code.length!==6||loading}
                  style={{flex:1,padding:'13px',borderRadius:12,border:'none',fontFamily:'inherit',fontWeight:900,fontSize:'.9rem',
                    cursor:code.length===6?'pointer':'not-allowed',
                    background:code.length===6?'#ef4444':'#e5e7eb',
                    color:code.length===6?'#fff':'#9ca3af',transition:'all .2s'}}>
                  {loading
                    ? <span style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
                        <span style={{width:14,height:14,border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',display:'inline-block',animation:'tfa-spin .6s linear infinite'}}/>
                        Disabling…
                      </span>
                    : '🔓 Disable Two-Factor Auth'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════
            DISABLE — STEP 2: Confirmed
            ═══════════════════════════════════ */}
        {mode==='disable' && step===2 && (
          <div className="tfa-step" style={{padding:'44px 32px',textAlign:'center'}}>
            <div style={{fontSize:64,lineHeight:1,marginBottom:16,animation:'tfa-pop .5s cubic-bezier(.34,1.56,.64,1)'}}>🔓</div>
            <h2 style={{fontWeight:900,fontSize:'1.4rem',color:'#111827',margin:'0 0 10px'}}>
              Two-Factor Auth Disabled
            </h2>
            <p style={{color:'#6b7280',lineHeight:1.7,marginBottom:24}}>
              Your account is now protected by password only.
              We recommend enabling 2FA again for maximum security.
            </p>
            <button onClick={onClose}
              style={{padding:'12px 32px',borderRadius:12,border:'none',background:'#374151',color:'#fff',fontFamily:'inherit',fontWeight:800,cursor:'pointer'}}>
              Close
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
